// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Hatch — the two policies the balance sim (and the in-browser objective-completable check)
// drive through the REAL game path:
//   · READER — a logical solver: it only ever fills squares that are truly filled, so it
//     makes ZERO mistakes and always reveals the picture. (It reads the solution; that the
//     picture is solvable by pure LOGIC with no guessing is proved separately by the line
//     solver at generation, asserted in the sim.)
//   · MASHER — a blind guesser: it fills random unknown squares with no deduction, so every
//     empty square it hits is a mistake. It completes Zen (no death) with a wrecked score,
//     and DIES in Strikes before it can finish. The gap between the two is the whole game.
// Both move at the SAME cadence, so their score difference is about mistakes, not speed.

import { makeRng, type Rng } from '@ben-gy/game-engine/rng';
import type { Mode } from './modes';
import { createGame, paint, tick, UNKNOWN, type GameState, type PaintAction } from './game';

export interface Policy {
  readonly id: string;
  readonly name: string;
}

export const READER: Policy = { id: 'reader', name: 'Reader' };
export const MASHER: Policy = { id: 'masher', name: 'Masher' };
export const POLICIES: Policy[] = [READER, MASHER];

export function policyOf(id: string): Policy {
  return POLICIES.find((p) => p.id === id) ?? READER;
}

const MOVE_MS = 150;

/** The next (cell, action) this policy would play, or null if it has nothing to do. */
export function nextMove(state: GameState, policy: Policy, rng: Rng): { idx: number; action: PaintAction } | null {
  if (state.over) return null;
  const sol = state.board.solution;
  const cells = state.cells;
  if (policy.id === 'reader') {
    // fill the first still-unknown truly-filled cell (deterministic sweep)
    for (let i = 0; i < cells.length; i++) {
      if (cells[i] === UNKNOWN && sol[i] === 1) return { idx: i, action: 'fill' };
    }
    return null; // board already complete (shouldn't be reached before onComplete fires)
  }
  // masher: fill a random unknown cell
  const unknown: number[] = [];
  for (let i = 0; i < cells.length; i++) if (cells[i] === UNKNOWN) unknown.push(i);
  if (unknown.length === 0) return null;
  return { idx: unknown[(rng() * unknown.length) | 0], action: 'fill' };
}

export interface BotOpts {
  rng?: Rng;
  moveMs?: number;
  maxMoves?: number;
}

/** Play a full solo run of `policy` from a seed through the real game path. */
export function playBot(mode: Mode, seed: number, policy: Policy, opts: BotOpts = {}): GameState {
  const rng = opts.rng ?? makeRng((seed ^ 0x9e3779b9) >>> 0);
  const moveMs = opts.moveMs ?? MOVE_MS;
  const maxMoves = opts.maxMoves ?? 100_000;
  const state = createGame(mode, seed);
  let moves = 0;
  while (!state.over && moves++ < maxMoves) {
    const mv = nextMove(state, policy, rng);
    if (!mv) {
      // no move available but not over — advance the clock so a down-clock can still end
      tick(state, moveMs);
      continue;
    }
    paint(state, mv.idx, mv.action);
    tick(state, moveMs);
  }
  return state;
}
